/**
 * lib/sql-probe.ts — Best-effort SQL Server reachability + database bootstrap for
 * `ss dev`. The dev seed proposes a per-branch database that may not exist yet; this
 * module probes the server via `sqlcmd` and CREATEs the database when it is missing,
 * so the backend's first boot connects to a real DB instead of failing.
 *
 * It mirrors the proven invocation idiom in `templates/skills/lib/sqlcmd.ts` (trusted
 * `-E` auth unless a SQL user is set; `-b` so SQL errors surface as a non-zero exit).
 * Everything degrades gracefully: sqlcmd absent or the server unreachable returns a
 * structured result the caller turns into a WARNING — it never throws into the dev
 * flow, because the EF migrations applied at startup still create the database/schema on boot.
 */
import { execFileSync, spawnSync } from 'node:child_process';
import { validateSqlDatabaseName } from './dev-configure.js';

export interface ProbeConn {
  server: string;
  database: string;
  user: string | null;
  password: string | null;
  useWindowsAuth: boolean;
  trustServerCertificate: boolean;
}

/**
 * Parse the connection bits `sqlcmd` needs from a `DefaultConnection` string.
 * Case-insensitive on keys; supports the `Server`/`Data Source`,
 * `Database`/`Initial Catalog`, `User Id`/`Uid`, `Password`/`Pwd`,
 * `Integrated Security`/`Trusted_Connection` and `TrustServerCertificate` aliases.
 * PURE.
 */
export function parseProbeConn(connStr: string): ProbeConn {
  const parts: Record<string, string> = {};
  for (const seg of (connStr ?? '').split(';')) {
    const i = seg.indexOf('=');
    if (i < 0) continue;
    const k = seg.slice(0, i).trim().toLowerCase();
    const v = seg.slice(i + 1).trim();
    if (k) parts[k] = v;
  }
  const integrated = (parts['integrated security'] ?? parts['trusted_connection'] ?? '').toLowerCase();
  const user = parts['user id'] || parts['uid'] || null;
  const useWindowsAuth = integrated === 'true' || integrated === 'sspi' || integrated === 'yes' || !user;
  return {
    server: parts['server'] || parts['data source'] || '(local)',
    database: parts['database'] || parts['initial catalog'] || 'master',
    user,
    password: parts['password'] || parts['pwd'] || null,
    useWindowsAuth,
    trustServerCertificate: (parts['trustservercertificate'] ?? '').toLowerCase() === 'true',
  };
}

/**
 * Build the `sqlcmd` argv for a query against a specific database (default `master`).
 * PURE so the argument contract is unit-testable. Auth is `-E` (trusted) unless a SQL
 * user is configured; `-b` makes sqlcmd exit non-zero on SQL error so failures are
 * detectable.
 */
export function buildProbeArgs(conn: ProbeConn, query: string, database = 'master'): string[] {
  const auth = conn.useWindowsAuth || !conn.user ? ['-E'] : ['-U', conn.user, '-P', conn.password ?? ''];
  const args = ['-S', conn.server, '-d', database, ...auth, '-b', '-h', '-1', '-W'];
  if (conn.trustServerCertificate) args.push('-C');
  args.push('-Q', query);
  return args;
}

export interface SqlProbeResult {
  /** Overall success: the database exists (or was created) and the server answered. */
  ok: boolean;
  /** `sqlcmd` resolved on PATH. False → check skipped, the EF migrations applied at startup will create the DB. */
  available: boolean;
  /** The server answered the reachability probe. */
  reachable: boolean;
  /** The database already existed before this run. */
  existed: boolean;
  /** We created the database this run. */
  created: boolean;
  error: string | null;
}

/** True when `sqlcmd` resolves on PATH. */
export function sqlcmdAvailable(sqlcmdPath = 'sqlcmd'): boolean {
  try {
    const r = spawnSync(sqlcmdPath, ['-?'], { encoding: 'utf-8', timeout: 5000, windowsHide: true });
    // ENOENT → not installed. Any other outcome (even a non-zero exit from `-?`) means
    // the binary is present.
    return !(r.error && (r.error as NodeJS.ErrnoException).code === 'ENOENT');
  } catch {
    return false;
  }
}

function describeError(e: unknown, conn: ProbeConn): string {
  const err = e as { code?: string; stderr?: string; stdout?: string; message?: string };
  if (err.code === 'ENOENT') return `'sqlcmd' not found on PATH.`;
  const detail = [err.stderr, err.stdout].filter(Boolean).join(' ').trim() || err.message || String(e);
  return `sqlcmd failed (server="${conn.server}"): ${detail.replace(/\s+/g, ' ').slice(0, 400)}`;
}

function runProbe(conn: ProbeConn, query: string, database: string, sqlcmdPath: string, timeout: number): string {
  return execFileSync(sqlcmdPath, buildProbeArgs(conn, query, database), {
    encoding: 'utf-8',
    timeout,
    windowsHide: true,
    maxBuffer: 8 * 1024 * 1024,
  });
}

const skip = (error: string): SqlProbeResult => ({
  ok: false,
  available: false,
  reachable: false,
  existed: false,
  created: false,
  error,
});

/**
 * Probe the server and ensure the target database exists, creating it if missing.
 *
 * Sequence: validate the DB name (identifier-safe for `CREATE DATABASE [..]`) →
 * confirm `sqlcmd` is installed → `SELECT 1` on `master` (reachability) →
 * `DB_ID(N'<db>')` (existence) → `CREATE DATABASE [<db>]` when absent.
 *
 * NEVER throws. Each failure mode returns a structured result the caller surfaces as
 * a warning:
 *   - `available:false`  → sqlcmd not installed (skip; the EF migrations applied at startup create the DB).
 *   - `reachable:false`  → server down / bad credentials.
 *   - `ok:true`          → DB existed (`existed`) or was created (`created`).
 */
export function ensureDatabaseExists(
  connStr: string,
  opts: { sqlcmdPath?: string; timeout?: number } = {}
): SqlProbeResult {
  const sqlcmdPath = opts.sqlcmdPath ?? 'sqlcmd';
  const timeout = opts.timeout ?? 30000;
  const conn = parseProbeConn(connStr);

  const nameErrors = validateSqlDatabaseName(conn.database);
  if (nameErrors.length > 0) {
    return { ok: false, available: true, reachable: false, existed: false, created: false, error: nameErrors.join(' ') };
  }
  if (!sqlcmdAvailable(sqlcmdPath)) {
    return skip(`'${sqlcmdPath}' not found on PATH — skipped the DB check (the EF migrations applied at startup will create it).`);
  }

  // 1) Reachability — SELECT 1 against master.
  try {
    runProbe(conn, 'SELECT 1', 'master', sqlcmdPath, timeout);
  } catch (e) {
    return { ok: false, available: true, reachable: false, existed: false, created: false, error: describeError(e, conn) };
  }

  // 2) Existence — DB_ID returns NULL when the database is absent.
  const dbLiteral = conn.database.replace(/'/g, "''"); // safe (name already validated), defensive
  let existed = false;
  try {
    const out = runProbe(
      conn,
      `SET NOCOUNT ON; SELECT CASE WHEN DB_ID(N'${dbLiteral}') IS NULL THEN 0 ELSE 1 END`,
      'master',
      sqlcmdPath,
      timeout
    );
    existed = /(^|\D)1(\D|$)/.test(out.trim());
  } catch (e) {
    return { ok: false, available: true, reachable: true, existed: false, created: false, error: describeError(e, conn) };
  }
  if (existed) {
    return { ok: true, available: true, reachable: true, existed: true, created: false, error: null };
  }

  // 3) Create — the name passed validateSqlDatabaseName, so [<name>] cannot inject.
  try {
    runProbe(
      conn,
      `IF DB_ID(N'${dbLiteral}') IS NULL CREATE DATABASE [${conn.database}]`,
      'master',
      sqlcmdPath,
      timeout
    );
    return { ok: true, available: true, reachable: true, existed: false, created: true, error: null };
  } catch (e) {
    return { ok: false, available: true, reachable: true, existed: false, created: false, error: describeError(e, conn) };
  }
}
